import React, { useState, useEffect } from 'react';
import { featureDesignStudioApi } from '../../services/featureDesignStudioApi';
import { projectApi } from '../../services/api';
import { featureTagApi } from '../../services/api';
import './BuildTasksModal.css';

export default function BuildTasksModal({ draft, onClose, onSuccess }) {
  const [projectId, setProjectId] = useState('');
  const [featureTagId, setFeatureTagId] = useState('');
  const [projects, setProjects] = useState([]);
  const [featureTags, setFeatureTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await projectApi.getAll();
        if (!cancelled) setProjects(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load projects');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!projectId) {
      setFeatureTags([]);
      setFeatureTagId('');
      return;
    }
    let cancelled = false;
    setLoadingTags(true);
    (async () => {
      try {
        const list = await featureTagApi.getByProject(projectId);
        if (!cancelled) setFeatureTags(Array.isArray(list) ? list : []);
        if (!cancelled) setFeatureTagId('');
      } catch (err) {
        if (!cancelled) setFeatureTags([]);
      } finally {
        if (!cancelled) setLoadingTags(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId || !draft) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await featureDesignStudioApi.buildTasks({
        draft,
        project_id: projectId,
        feature_tag_id: featureTagId || undefined
      });
      setSuccess(`${result.created_count ?? 0} task(s) created.`);
      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 1500);
    } catch (err) {
      const data = err.response?.data;
      const message = data?.error || err.message || 'Failed to create tasks';
      const details = data?.details || data?.link_errors;
      setError(details ? `${message} ${typeof details === 'string' ? details : JSON.stringify(details)}` : message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fds-modal-backdrop" onClick={onClose} role="presentation">
      <div className="fds-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="fds-modal-header">
          <h3 className="fds-modal-title">Build Tasks</h3>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
        </div>
        <form onSubmit={handleSubmit} className="fds-modal-body">
          <p className="fds-modal-intro">
            Create tasks in the project from this plan. Only managers can create project tasks.
          </p>
          <div className="mb-3">
            <label className="form-label" htmlFor="fds-build-project">
              Project *
            </label>
            <select
              id="fds-build-project"
              className="form-select"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">Select project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.abbreviation || p.id}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="fds-build-feature-tag">
              Feature tag (optional)
            </label>
            <select
              id="fds-build-feature-tag"
              className="form-select"
              value={featureTagId}
              onChange={(e) => setFeatureTagId(e.target.value)}
              disabled={loading || loadingTags || !projectId}
            >
              <option value="">None</option>
              {featureTags.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <div className="alert alert-danger py-2 mb-3" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success py-2 mb-3" role="alert">
              {success}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-success" disabled={loading || !projectId}>
              {loading ? 'Creating…' : 'Create Tasks'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
